import { formatInrAmount } from "./formatInrAmount";
import { giftLidPositionLabel } from "../components/GiftBoxLidEditor";

/** Shared cart totals and WhatsApp message text for cart + checkout. */

/**
 * @returns {{ subtotal: number }}
 * `subtotal` = line items only. Shipping is added at checkout.
 */
export function getCartTotals(cart) {
  const subtotal = cart.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0,
  );
  return { subtotal };
}

/**
 * @returns {{
 *   itemsSubtotal: number,
 *   merchandiseTotal: number,
 *   shipping: number,
 *   orderPayableTotal: number,
 * }}
 */
export function getOrderTotals({
  cart,
  voucherDiscount = 0,
  couponDiscount = 0,
  shippingCost = 0,
}) {
  const { subtotal: itemsSubtotal } = getCartTotals(cart);
  const merchandiseTotal = Math.max(
    0,
    itemsSubtotal - Math.max(0, voucherDiscount) - Math.max(0, couponDiscount),
  );
  const shipping = Math.max(0, Number(shippingCost) || 0);
  const orderPayableTotal = merchandiseTotal + shipping;
  return { itemsSubtotal, merchandiseTotal, shipping, orderPayableTotal };
}

function sanitizeSummaryText(s) {
  return String(s || "")
    .replace(/\|/g, "·")
    .replace(/\s+/g, " ")
    .trim();
}

function formatLidLayout(layout) {
  if (!layout || typeof layout !== "object") return "";
  const sc = Number(layout.scale);
  if (!Number.isFinite(sc)) return "";
  return ` [Gift box position: ${giftLidPositionLabel(layout)}, ${sc.toFixed(2)}×]`;
}

function formatLidTextColor(color) {
  if (color === "silver") return " [Gift box text: silver]";
  if (color === "gold") return " [Gift box text: gold]";
  return "";
}

export function buildItemsSummary(cart) {
  return cart
    .map((item) => {
      const base = `${item.name} (ID: ${item.id}) × ${item.quantity} @ ₹${formatInrAmount(item.price)}`;
      const lid = item.giftLidMessage ? ` — Gift box: ${sanitizeSummaryText(item.giftLidMessage)}` : "";
      const lay = formatLidLayout(item.giftLidLayout);
      const col = formatLidTextColor(item.giftLidTextColor);
      return `${base}${lid}${lay}${col}`;
    })
    .join(" | ");
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(value) {
  const s = String(value || "").trim();
  return s.length > 0 && EMAIL_RE.test(s);
}

export function buildWhatsAppMessage(customer, cart, totals) {
  const { subtotal } = totals;
  const lines = [
    "Hello, I would like to place an order.",
    "",
    `Name: ${customer.name.trim()}`,
    `Email: ${(customer.email || "").trim()}`,
    `Phone: ${customer.phone.trim()}`,
    `Address: ${customer.address.trim()}`,
    `Pincode: ${customer.pincode.trim()}`,
    "",
    "Items:",
  ];
  cart.forEach((item, index) => {
    const lid = item.giftLidMessage
      ? ` — Gift box message: ${String(item.giftLidMessage).replace(/\s+/g, " ").trim()}`
      : "";
    const lay = formatLidLayout(item.giftLidLayout);
    const col = formatLidTextColor(item.giftLidTextColor);
    lines.push(
      `${index + 1}. ${item.name} — Qty: ${item.quantity} — ₹${formatInrAmount(item.price)} each${lid}${lay}${col}`,
    );
  });
  lines.push("");
  lines.push(`Total: ₹${formatInrAmount(subtotal)}`);
  return lines.join("\n");
}
